import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { workQueueApi, type WorkQueueQuery } from '@/api/workQueue.api';
import { PageHeader } from '@/components/layout/PageHeader';
import { KpiGrid } from '@/components/ui/KpiGrid';
import { StatCard } from '@/components/ui/StatCard';
import { Select } from '@/components/ui/Select';
import { PageSection } from '@/components/ui/PageSection';
import { WorkQueueItemCard } from '@/components/workqueue/WorkQueueItemCard';
import type { WorkQueueBucket, WorkQueueListResponse } from '@/types/workQueue';
import { showApiError } from '@/utils/apiError';

const BUCKETS: { id: WorkQueueBucket | 'ALL'; label: string }[] = [
  { id: 'ALL', label: 'All open' },
  { id: 'REQUIRES_ATTENTION', label: 'Requires attention' },
  { id: 'DUE_TODAY', label: 'Due today' },
  { id: 'OVERDUE', label: 'Overdue' },
  { id: 'WAITING_APPROVAL', label: 'Waiting for approval' },
  { id: 'WAITING_RESPONSE', label: 'Waiting for response' },
];

export function MyWorkPage() {
  const [data, setData] = useState<WorkQueueListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState<WorkQueueQuery['scope']>('ALL');
  const [module, setModule] = useState('');
  const [bucket, setBucket] = useState<WorkQueueBucket | 'ALL'>('ALL');
  const [priority, setPriority] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await workQueueApi.list({
        scope,
        module: module || undefined,
        bucket: bucket === 'ALL' ? undefined : bucket,
        priority: priority || undefined,
      });
      setData(res);
    } catch (err) {
      showApiError(err, 'Failed to load My Work');
    } finally {
      setLoading(false);
    }
  }, [scope, module, bucket, priority]);

  useEffect(() => {
    void load();
  }, [load]);

  const filteredItems = useMemo(() => data?.items ?? [], [data]);

  return (
    <div className="page-content">
      <PageHeader
        title="My Work"
        description="Your daily action queue across Finance, HR, CRM, Desk, Projects, Social, Marketing, and more"
        actions={
          <div className="flex items-center gap-4">
            <Link
              to="/work/approvals"
              className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
              All approvals →
            </Link>
            <Link
              to="/notifications"
              className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
              Notifications →
            </Link>
            <Link
              to="/work/executive"
              className="text-sm font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
              Executive view →
            </Link>
          </div>
        }
      />

      {data?.summary && (
        <KpiGrid columns={4} className="mb-6">
          <StatCard title="Requires attention" value={data.summary.requiresAttention} />
          <StatCard title="Due today" value={data.summary.dueToday} />
          <StatCard title="Overdue" value={data.summary.overdue} />
          <StatCard title="Waiting approval" value={data.summary.waitingApproval} />
          <StatCard title="Waiting response" value={data.summary.waitingResponse} />
          <StatCard title="Assigned to me" value={data.summary.assignedToMe} />
          <StatCard title="Total open" value={data.summary.totalOpen} />
          <StatCard title="View" value={data.roleView} />
        </KpiGrid>
      )}

      <div className="card mb-6 grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <Select
          label="Scope"
          value={scope ?? 'ALL'}
          onChange={(v) => setScope(v as WorkQueueQuery['scope'])}
          options={[
            { value: 'ALL', label: 'All items' },
            { value: 'MY', label: 'My items' },
          ]}
        />
        <Select
          label="Module"
          value={module}
          onChange={setModule}
          options={[
            { value: '', label: 'All modules' },
            { value: 'FINANCE', label: 'Finance' },
            { value: 'HR', label: 'HR' },
            { value: 'CRM', label: 'CRM' },
            { value: 'DESK', label: 'Desk' },
            { value: 'PROJECTS', label: 'Projects' },
            { value: 'SOCIAL', label: 'Social' },
            { value: 'MARKETING', label: 'Marketing' },
            { value: 'POS', label: 'Inventory / POS' },
          ]}
        />
        <Select
          label="Priority"
          value={priority}
          onChange={setPriority}
          options={[
            { value: '', label: 'Any priority' },
            { value: 'CRITICAL', label: 'Critical' },
            { value: 'HIGH', label: 'High' },
            { value: 'MEDIUM', label: 'Medium' },
            { value: 'LOW', label: 'Low' },
          ]}
        />
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {BUCKETS.map((b) => (
          <button
            key={b.id}
            type="button"
            onClick={() => setBucket(b.id)}
            className={`rounded-full px-3 py-1 text-sm font-medium transition ${
              bucket === b.id
                ? 'bg-primary-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300'
            }`}
          >
            {b.label}
          </button>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <PageSection title="Work items">
          {loading && <p className="text-sm text-slate-500">Loading…</p>}
          {!loading && filteredItems.length === 0 && (
            <div className="card p-8 text-center text-sm text-slate-500">
              No items match your filters. Try All open or another module.
            </div>
          )}
          <div className="space-y-3">
            {filteredItems.map((item) => (
              <WorkQueueItemCard key={item.id} item={item} onActionComplete={load} />
            ))}
          </div>
        </PageSection>

        <aside className="space-y-4">
          <PageSection title="Smart recommendations" description="AI-ready insights — reserved for future prioritization">
            <ul className="space-y-3">
              {(data?.recommendations ?? []).map((r, i) => (
                <li key={i} className="card p-3 text-sm">
                  <p className="font-medium">{r.title}</p>
                  <p className="mt-1 text-slate-600 dark:text-slate-400">{r.detail}</p>
                  <Link to={r.deepLink} className="mt-2 inline-block text-xs text-primary-600 hover:underline">
                    View →
                  </Link>
                </li>
              ))}
            </ul>
          </PageSection>
        </aside>
      </div>
    </div>
  );
}
